import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { ROUTES } from '@lsgo/lsgo-common/constants/routes';

/**
 * Options for useAuthCheck hook
 */
export interface UseAuthCheckOptions {
    requiresAuth?: boolean;
    allowExpired?: boolean;
    embed?: boolean;
    redirectTo?: string;
}

/**
 * Centralized authentication and expiration check hook
 * Returns a redirect component if auth fails, or null if auth passes
 */
export const useAuthCheck = ({
    requiresAuth = true,
    allowExpired = false,
    embed = false,
    redirectTo,
}: UseAuthCheckOptions = {}): React.ReactElement | null => {
    const location = useLocation();
    const auth = useAuth({ embed });

    // Public routes (embed mode) - always allow
    if (embed) {
        return null;
    }

    // Check authentication
    if (requiresAuth && !auth.isAuthenticated) {
        const loginPath = redirectTo || `/?redirectUri=${encodeURIComponent(location.pathname + location.search)}`;
        return <Navigate to={loginPath} replace />;
    }

    // Check account expiration
    if (requiresAuth && !allowExpired && auth.isExpired && !location.pathname.includes(ROUTES.TEACHER.PROFILE.path)) {
        return <Navigate to={ROUTES.TEACHER.PROFILE.path} replace />;
    }

    // All auth checks passed
    return null;
};
